// The entire application has a few high-level states:
// - Start: nothing is going on, the user can choose how to begin
// - Starting up: a model or simulation is chosen, the necessary API calls are made
// - Naming a simulation: a model is chosen and we need to name the simulation
// - Main: we are looking at and interacting with a simulation

// These decide which high-level screen to show in App.tsx.

// This Redux sub-part contains *no own Redux state* and no reducer.

// It does contain a selector that decides the high-level state we are in,
// based on other parts of the Redux state.

// And it has thunks that represent various ways the application
// starts up to get into the Main state. Consider this as mostly
// imperative code that lets the application start.

// When starting the application (either by creating a simulation or
// by following an existing one) a series of things need to be set up
// in the right order. Before we go to the main screen:
// - The simulation must exist, we need to have the GeoJSON and grid
// - The simulation must be in 'initialized' state
// - We need an open Main websocket (only allowed when simulation is initialized)
// When these are met, we should go to the 'main' state.

// There are a few error conditions:
// - Failure to create the simulation (for an unspecified reason)
// - Not enough available sessions
// - Too many followers for a simulation
// And we need to handle these (mostly by going back to the main screen,
// with an error message in the snackbar).

// The two ways to start have their own functions:

// startWithOrganisationAndModel
// This way creates its own simulation and must take care not to continue
// before it really is initialized. We know this over the Websocket.
// Steps to take:
// - Create the simulation, download the geojson and grid
// - Initialize the simulation
//   If that doesn't return 200, there were no free sessions.
// - Open the Websocket
//   If that fails, there were too many websockets (should not happen
//   right after creation).
// - Continue when the Websocket communicates that the simulation state
//   is initialized. The Websocket sends the current state immediately
//   on opening, and changes after that. This is actually handled
//   in simulation.ts' reducer, which sets 'startupReady' to true in
//   this case.

// startWithSimulation
// As this only works for initialized simulations we can do things in a
// simpler order.
// - Retrieves the simulation from the backend
//   If it is not 'initialized', show an error
// - Retrieve the model and organisation
// - Retrieve the GeoJSON and grid
// - As we know the simulation is initialized, just open the Websocket

import { Feature, FeatureCollection } from "geojson";
import { FormattedMessage } from "react-intl";

import * as api from "@nens/threedi/openapi-client";
import { Organisation, Template } from "@nens/threedi/openapi-client";

import { getV3Api } from "../apiConfiguration";
import { AppDispatch, AppState, Thunk } from "../App";
import { ControlType, checkPermissionStateless } from "../utils/authorizationUtils";
import { getBounds } from "../utils/geoUtils";
import { getCurrentTimestamp } from "../utils/timeConverter";

import { setList, setStatus } from "./../state/layerRasterSourcesMenu";
import { sendMessage } from "./mainGui";
import { getUser } from "./session";
import {
  ENDLESS_SIMULATION_DURATION_LIMIT,
  PAUSE_TIMEOUT_SECONDS,
  getSimulation,
  getSimulationStatus as getSimulationStatusFromRedux,
  openWebsocket,
  receiveGeojson,
  receiveGridCells,
  receiveGridFlowLines,
  setSelectedOrganisationAndThreediModel,
  setSimulation,
  startupFailed,
  validateExtent,
} from "./simulation";
import { closeAllWebsockets } from "./websockets";

export type HighLevelState = "START" | "STARTING UP" | "MAIN" | "DOWNLOAD";

// Selector for High Level State
export const getHighLevelState = (state: AppState): HighLevelState => {
  const simul = state.simulation;
  if (simul.startupReady) {
    if (
      simul.downloadSimulationResults &&
      (simul.status === "postprocessing" || simul.status === "finished")
    ) {
      return "DOWNLOAD";
    }

    return "MAIN";
  } else if (simul.threediModel || simul.simulation) {
    return "STARTING UP";
  } else {
    return "START";
  }
};

const validate3diModel = (threediModel: api.ThreediModel) => {
  if (
    !(
      // @ts-ignore: Swagger mistakenly says extentOneD is a string. https://github.com/nens/threedi-api/issues/666
      (
        (threediModel.extentOneD &&
          threediModel.extentOneD.coordinates &&
          validateExtent([
            [threediModel.extentOneD.coordinates[0][0], threediModel.extentOneD.coordinates[0][1]],
            [threediModel.extentOneD.coordinates[1][0], threediModel.extentOneD.coordinates[1][1]],
          ])) ||
        // @ts-ignore: Swagger mistakenly says extentOneD is a string. https://github.com/nens/threedi-api/issues/666
        (threediModel.extentTwoD &&
          threediModel.extentTwoD.coordinates &&
          validateExtent([
            [threediModel.extentTwoD.coordinates[0][0], threediModel.extentTwoD.coordinates[0][1]],
            [threediModel.extentTwoD.coordinates[1][0], threediModel.extentTwoD.coordinates[1][1]],
          ]))
      )
    )
  ) {
    return {
      valid: false,
      msg: `error.no_valid_model_bounds`,
    };
  }

  return {
    valid: true,
    msg: "",
  };
};

// Thunk actions for ways to start up the application
export const startWithOrganisationAndModel =
  (parOrganisation: api.Organisation, parThreediModel: api.ThreediModel): Thunk =>
  (dispatch, getState) => {
    const modelValid = validate3diModel(parThreediModel);
    if (modelValid.valid) {
      dispatch(setSelectedOrganisationAndThreediModel(parOrganisation, parThreediModel));
    } else {
      dispatch(backToStartScreen());
      const miliseconds = 10000;
      dispatch(sendMessage(modelValid.msg, miliseconds, "error"));
      return;
      // below line does not display an error message but instead crashes the app, which makes me doubt if the errors thrown below (line 170) will work.
      // Since in the function 'startWithSimulation' the invalid bounds message is also send by throwing an error I tried to use that here as well, but without succes.
      // throw new Error(modelValid.msg)
    }

    const threediModel = getState().simulation.threediModel;
    const organisation = getState().simulation.selectedOrganisation;

    if (threediModel === null) {
      throw new Error("No threedimodel found");
    }
    if (organisation === null) {
      throw new Error("No organisation found");
    }
    if (threediModel.id === undefined) return;
    const threediModelId = threediModel.id;

    const simulationName = threediModel.name
      ? `${threediModel.name}_${getCurrentTimestamp()}`
      : getCurrentTimestamp();

    Promise.all([
      createSimulation(dispatch, threediModelId, organisation.uniqueId, simulationName),
      downloadGeojson(dispatch, threediModelId),
      downloadGridCells(dispatch, threediModelId),
      downloadGridFlowLines(dispatch, threediModelId),
      retrieveConfiguredRasters(dispatch, threediModelId),
    ]).then(() => {
      const simulation = getSimulation(getState());
      if (simulation === null) {
        return;
      }
      const simulationId = simulation.id;
      if (simulationId === undefined) return;
      // START Add initial waterdepth to simulation
      const v3Api = getV3Api();

      // set initial waterlevel 1d. When?: always
      v3Api.simulationsInitial1dWaterLevelPredefinedCreate({
        simulationPk: simulationId,
        data: {},
      });

      const initialWaterlevelPromises: Promise<api.InitialWaterlevel>[] = [];
      const initialGroundWaterlevelPromises: Promise<api.GroundWaterRaster>[] = [];
      const initialrasterReadPromises: Promise<api.Raster>[] = [];

      // set initial waterlevel 2d. When?: if initial waterlevel raster present
      v3Api
        .threedimodelsInitialWaterlevelsList({
          threedimodelPk: threediModelId,
        })
        .then((response) => {
          response.results.forEach((item) => {
            if (
              item.file &&
              item.file.type === api.UploadTypeEnum.INITIALWATERLEVEL &&
              item.id &&
              item.sourceRasterId
            ) {
              const initialRasterreadPromise = v3Api.threedimodelsRastersRead({
                id: item.sourceRasterId,
                threedimodelPk: threediModelId,
              });
              initialrasterReadPromises.push(initialRasterreadPromise);
              initialRasterreadPromise.then((parsedResponse) => {
                if (parsedResponse.type === "initial_waterlevel_file") {
                  initialWaterlevelPromises.push(
                    v3Api.simulationsInitial2dWaterLevelRasterCreate({
                      simulationPk: simulationId,
                      data: {
                        aggregationMethod: api.AggregationMethodEnum.MAX,
                        initialWaterlevel: "" + item.id,
                      },
                    })
                  );
                } else if (parsedResponse.type === "initial_groundwater_level_file") {
                  initialGroundWaterlevelPromises.push(
                    v3Api.simulationsInitialGroundwaterLevelRasterCreate({
                      simulationPk: simulationId,
                      data: {
                        aggregationMethod: api.AggregationMethodEnum.MAX,
                        initialWaterlevel: "" + item.id,
                      },
                    })
                  );
                }
              });
            }
          });

          const allInitialRasterPromises = initialWaterlevelPromises.concat(
            initialGroundWaterlevelPromises,
            initialrasterReadPromises as Promise<api.InitialWaterlevel>[]
          );
          Promise.all(allInitialRasterPromises)
            .then(() => {
              const v3Api = getV3Api();
              v3Api
                .simulationsActionsCreate({
                  simulationPk: simulationId,
                  data: {
                    name: api.ActionNameEnum.INITIALIZE,
                    timeout: PAUSE_TIMEOUT_SECONDS,
                  },
                })
                .then(() => {
                  dispatch(openWebsocket());
                })
                .catch((error) => {
                  error.response.json().then(
                    (parsed: {
                      detail: string;
                      errors:
                        | undefined
                        | {
                            physical_settings: string;
                            numerical_settings: string;
                            time_step_settings: string;
                          };
                    }) => {
                      console.error("Initializing simulation failed with:", parsed);
                      const errorMessage = parsed.detail;
                      if (
                        errorMessage &&
                        errorMessage.indexOf &&
                        errorMessage.indexOf("has no more sessions available. Try again later") > -1
                      ) {
                        dispatch(
                          startupFailed(
                            <FormattedMessage
                              id="initialize_error.no_more_sessions_available"
                              defaultMessage="Organisation {organisation} has no more sessions available. Try again later."
                              values={{ organisation: organisation.name }}
                            />
                          )
                        );
                      } else if (
                        parsed.errors &&
                        parsed.errors.physical_settings === "Physical settings are missing" &&
                        parsed.errors.numerical_settings === "Numerical settings are missing" &&
                        parsed.errors.time_step_settings === "Time step settings are missing"
                      ) {
                        dispatch(
                          startupFailed(
                            <FormattedMessage
                              id="initialize_error.no_template_for_3dimodel"
                              defaultMessage="An error occured initializing the simulation. Most likely this Rana Model has no template available."
                            />
                          )
                        );
                      } else if (errorMessage) {
                        dispatch(
                          startupFailed(
                            "error.failed_to_initialize_simulation with error: " + errorMessage
                          )
                        );
                      } else {
                        dispatch(
                          startupFailed(
                            "error.failed_to_initialize_simulation with error: " +
                              JSON.stringify(parsed, null, "  ")
                          )
                        );
                      }
                    }
                  );
                });
            })
            .catch((error) => {
              error.response.json().then((parsed: { detail: string }) => {
                console.error("Creating simulation failed with:", parsed);
                const errorMessage = parsed.detail;
                dispatch(
                  startupFailed("error.failed_to_create_simulation with error: " + errorMessage)
                );
              });
            });
        });
    });
  };

export const startWithOrganisationThreediModelAndSimulationTemplate =
  (
    organisation: api.Organisation,
    threediModel: api.ThreediModel,
    simulationTemplate: api.Template
  ): Thunk =>
  async (dispatch) => {
    const v3Api = getV3Api();

    const modelValid = validate3diModel(threediModel);
    if (modelValid.valid) {
      dispatch(setSelectedOrganisationAndThreediModel(organisation, threediModel));
    } else {
      dispatch(backToStartScreen());
      const miliseconds = 10000;
      dispatch(sendMessage(modelValid.msg, miliseconds, "error"));
      return;
    }

    const simulationName = threediModel.name
      ? `${threediModel.name}_${getCurrentTimestamp()}`
      : getCurrentTimestamp();

    const threediModelId = threediModel.id!;

    const [simulation] = await Promise.all([
      createSimulationFromTemplate(dispatch, simulationTemplate, organisation, simulationName),
      downloadGeojson(dispatch, threediModelId),
      downloadGridCells(dispatch, threediModelId),
      downloadGridFlowLines(dispatch, threediModelId),
      retrieveConfiguredRasters(dispatch, threediModelId),
    ]);

    if (simulation === null) {
      dispatch(backToStartScreen());
      const milliseconds = 10000;
      dispatch(sendMessage("Failed to create simulation from template.", milliseconds, "error"));
      return;
    }
    const startSimulationId = simulation.id;
    if (startSimulationId === undefined) return;

    try {
      await v3Api.simulationsActionsCreate({
        simulationPk: startSimulationId,
        data: {
          name: api.ActionNameEnum.INITIALIZE,
          timeout: PAUSE_TIMEOUT_SECONDS,
        },
      });

      dispatch(openWebsocket());
    } catch (error: any) {
      const parsed = await error.response.json();

      console.error("Initializing simulation failed with:", parsed);
      const errorMessage = parsed.detail;

      if (errorMessage.indexOf("has no more sessions available. Try again later") > -1) {
        dispatch(
          startupFailed(
            <FormattedMessage
              id="initialize_error.no_more_sessions_available"
              defaultMessage="Organisation {organisation} has no more sessions available. Try again later."
              values={{ organisation: organisation.name }}
            />
          )
        );
      } else {
        dispatch(
          startupFailed("error.failed_to_initialize_simulation with error: " + errorMessage)
        );
      }
    }

    return simulation || null;
  };

export const startWithTemplate = async (
  modelId: number,
  organisationId: string,
  simulationTemplateId: number,
  dispatch: AppDispatch
) => {
  const v3api = getV3Api();

  const threediModel = await v3api.threedimodelsRead({ id: modelId });
  const organisation = await v3api.organisationsRead({
    uniqueId: organisationId,
  });
  const simulationTemplate = await v3api.simulationTemplatesRead({
    id: simulationTemplateId,
  });

  if (threediModel && organisation && simulationTemplate) {
    dispatch(
      startWithOrganisationThreediModelAndSimulationTemplate(
        organisation,
        threediModel,
        simulationTemplate
      )
    );
  }
};

export const startWithSimulation =
  (simulation: api.Simulation): Thunk =>
  async (dispatch) => {
    dispatch(setSimulation(simulation));

    try {
      if (!simulation.threedimodelId) {
        throw new Error("Simulation has no threedimodel");
      }

      const status = await getSimulationStatus(simulation);

      if (status !== "created" && status !== "starting" && status !== "initialized") {
        throw new Error("error.simulation_not_running");
      }

      const [threediModel, organisation] = await Promise.all([
        // Get model, organisation
        getThreediModel(simulation.threedimodelId),
        getOrganisation(simulation.organisation),
        retrieveConfiguredRasters(dispatch, simulation.threedimodelId),
      ]);

      const modelValid = validate3diModel(threediModel);

      if (modelValid.valid) {
        dispatch(setSelectedOrganisationAndThreediModel(organisation, threediModel));
      } else {
        dispatch(backToStartScreen());
        // we need an error to break out of the promise chain
        // therefore we do not use "sendmessage"
        throw new Error(modelValid.msg);
      }

      await Promise.all([
        // Get geojson
        downloadGeojson(dispatch, threediModel.id!),
        downloadGridCells(dispatch, threediModel.id!),
        downloadGridFlowLines(dispatch, threediModel.id!),

        // Get edits
      ]);

      dispatch(openWebsocket());
      // No longer dispatch this startupSuccess, since we need for the main websocket message create initialized instead
    } catch (error: any) {
      console.error("Connecting to simulation failed with:", error);
      // previously this block always logged the same error. In case the error.msg is usefull I need it to display yhe usefull error message.
      if (typeof error.message === "string") {
        dispatch(startupFailed(error.message));
      } else {
        dispatch(startupFailed("error.failed_to_connect_to_simulation"));
      }
    }
  };

// Promise-returning helper functions (building blocks of the functions above)

const createSimulation = async (
  dispatch: AppDispatch,
  threediModelId: number,
  organisationUniqueId: string,
  simulationName: string
) => {
  const v3Api = getV3Api();

  const simulation: api.Simulation = {
    name: simulationName,
    threedimodel: "" + threediModelId,
    organisation: organisationUniqueId,
    startDatetime: new Date(),
    duration: ENDLESS_SIMULATION_DURATION_LIMIT,
  };

  try {
    const createdSimulation = await v3Api.simulationsCreate({
      data: simulation,
    });

    if (!createdSimulation || !createdSimulation.id) {
      throw new Error("no valid simulation created");
    }

    dispatch(setSimulation(createdSimulation));
    return createdSimulation;
  } catch (error: any) {
    try {
      const parsed = await error.response.json();
      Object.values(parsed).forEach((errors: unknown) => {
        (errors as string[]).forEach((error) => dispatch(sendMessage(error, 10000, "error")));
      });
    } catch (errorParsingJson) {
      console.error("starting failed with error", error);
    }
    dispatch(backToStartScreen());
    return null;
  }
};

const createSimulationFromTemplate = async (
  dispatch: AppDispatch,
  simulationTemplate: Template,
  organisation: Organisation,
  simulationName: string
) => {
  const v3api = getV3Api();

  const simulationFromTemplate: api.FromTemplate = {
    name: simulationName,
    template: "" + simulationTemplate.id,
    organisation: organisation.uniqueId,
    startDatetime: new Date(),
    duration: ENDLESS_SIMULATION_DURATION_LIMIT,
    startedFrom: "3Di Live",
  };

  try {
    const createdSimulation = await v3api.simulationsFromTemplate({
      data: simulationFromTemplate,
    });

    if (!createdSimulation || !createdSimulation.id) {
      throw new Error("no valid simulation created");
    }

    dispatch(setSimulation(createdSimulation));
    return createdSimulation;
  } catch (error: any) {
    try {
      const parsed = await error.response.json();
      Object.values(parsed).forEach((errors: unknown) => {
        (errors as string[]).forEach((error) => dispatch(sendMessage(error, 10000, "error")));
      });
    } catch (errorParsingJson) {
      console.error("starting failed with error", error);
    }
    dispatch(backToStartScreen());
    return null;
  }
};

const getSimulationStatus = async (simulation: api.Simulation) => {
  const v3Api = getV3Api();
  const statusSimulationId = simulation.id;
  if (statusSimulationId === undefined) return undefined;

  const response = await v3Api.simulationsStatusList({
    simulationPk: statusSimulationId,
  });

  return response.name;
};

const getThreediModel = async (threediModelId: number) => {
  const v3Api = getV3Api();

  return await v3Api.threedimodelsRead({
    id: threediModelId,
  });
};

const getOrganisation = (organisationUniqueId: string) => {
  const v3Api = getV3Api();

  return v3Api.organisationsRead({
    uniqueId: organisationUniqueId,
  });
};

const downloadGeojson = async (dispatch: AppDispatch, threediModelId: number) => {
  const v3Api = getV3Api();

  try {
    const response = await v3Api.threedimodelsGeojsonDownload({
      id: threediModelId,
    });

    if (!response.getUrl) {
      throw new Error("geojson contained no getUrl field");
    }

    const getUrlResponse = await fetch(response.getUrl);
    const geojson = await getUrlResponse.json();
    dispatch(receiveGeojson(fixGeojson(geojson)));
    return geojson;
  } catch (e) {
    console.error("Download geojson file failed with:", e);
    return null;
  }
};

const downloadGridCells = async (dispatch: AppDispatch, threediModelId: number) => {
  const v3Api = getV3Api();
  try {
    const urlResponse = await v3Api.threedimodelsGeojsonCellsDownload({
      id: threediModelId,
    });
    if (!urlResponse.getUrl) {
      throw new Error("geojson gridcells contained no getUrl field");
    }
    const gridcellsResponse = await fetch(urlResponse.getUrl);
    const gridcells = await gridcellsResponse.json();
    dispatch(receiveGridCells(gridcells));
  } catch (e) {
    console.error("Download gridcells failed with:", e);
  }
};
const downloadGridFlowLines = async (dispatch: AppDispatch, threediModelId: number) => {
  const v3Api = getV3Api();
  try {
    const urlResponse = await v3Api.threedimodelsGeojsonFlowlinesDownload({
      id: threediModelId,
    });
    if (!urlResponse.getUrl) {
      throw new Error("geojson gridflowlines contained no getUrl field");
    }
    const response = await fetch(urlResponse.getUrl);
    const results = await response.json();
    dispatch(receiveGridFlowLines(results));
  } catch (e) {
    console.error("Download gridflowlines failed with:", e);
  }
};

const retrieveConfiguredRasters = async (dispatch: AppDispatch, threediModelId: number) => {
  try {
    dispatch(setStatus("", "SENT"));
    const v3Api = getV3Api();
    const response = await v3Api.threedimodelsRastersList({
      threedimodelPk: threediModelId,
      limit: 100,
    });

    dispatch(setList(response.results, "", "SUCCESS"));
    return response.results;
  } catch (error) {
    dispatch(
      setStatus(
        "retrieving existing rain events failed with following error. Check console and network tab" +
          error,
        "FAILED"
      )
    );
    console.error("retrieving existing rain events failed with following error  ____", error);
  }
};

// Promise-returning helper functions to check if simulation name has been used already

export const getSimulationByName = async (simulationName: string) => {
  const v3Api = getV3Api();

  return await v3Api.simulationsList({
    name: simulationName,
  });
};

// Other helper functions

const fixGeojson = (geoJson: FeatureCollection): FeatureCollection => {
  // We still do some fixes on the incoming GeoJSON:
  //
  // - Model_types like 'Breaches' have a capital at the start, this is ugly
  //   and not what the Websocket events do. We make them lower case.
  //
  // - There is overlap between 'id' fields of the GeoJSON. We add
  //   a 'site_id': string field that consists of model_type.lower() + '_' + id
  //   (e.g. 'breaches_1') so that we have unique ids to work with for e.g. clicks.
  //
  // - There are both Manholes and Nodes with the property "is_manhole": 1. It turns
  //   out these are the same objects, listed twice. We filter out the nodes with
  //   that property.
  //
  // - It is sometimes useful for performance reasons to have a bounding box
  //   for a feature. We add a 'bounds' property here with type Leaflet.LatLngBounds.
  //
  // - For searching, we add a 'searchString' that contains a lower case string to
  //   match against search terms.
  //
  // Note the below isn't strictly functional as it mutates the features passed in,
  // which should be OK because it is only used once and no references to the old
  // values are kept afterwards.
  const features: Feature[] = geoJson.features.filter((f) => f !== null);

  return {
    ...geoJson,
    features: features
      .filter(
        (feature: Feature) =>
          feature.properties!.model_type !== "Nodes" || !feature.properties!.is_manhole
      )
      .map((feature: Feature) => {
        feature.properties!.model_type = feature.properties!.model_type.toLowerCase();
        feature.properties!.site_id = `${feature.properties!.model_type}_${feature.properties!.id}`;
        feature.properties!.bounds = getBounds(feature);
        feature.properties!.searchString = `${feature.properties!.model_type || ""} ${
          feature.properties!.display_name || ""
        } ${feature.properties!.code || ""} ${
          feature.properties!.model_type === "breaches"
            ? feature.properties!.content_pk
            : feature.properties!.id
        }`.toLowerCase();

        return feature;
      }),
  };
};

export const checkPermission = (state: AppState) => (controlType: ControlType) => {
  const user = getUser(state);
  const simulation = getSimulation(state);
  const status = getSimulationStatusFromRedux(state).status;
  return checkPermissionStateless(controlType, user, simulation, status);
};

export const backToStartScreen = (): Thunk => (dispatch) => {
  // Close all websocket connections before going back to start screen
  dispatch(closeAllWebsockets());
  dispatch({
    type: BACK_TO_START_SCREEN,
  });
};

export const BACK_TO_START_SCREEN = "simulation/BACK_TO_START_SCREEN";

export interface BackToStartScreen {
  type: typeof BACK_TO_START_SCREEN;
}
